<template>
  <div class="page container p-4 py-4">
    <!-- ── Pills de status dinâmicas ───────────────────────── -->
    <div v-if="!loading && allEvents.length > 0" class="status-pills mb-4">
      <p class="status-pills__label">FILTRAR POR STATUS</p>
      <div class="status-pills__list">
        <button
          v-for="pill in availablePills"
          :key="pill.key"
          class="status-pill"
          :class="{ 'status-pill--active': activeStatus === pill.key,
                    'status-pill--draft':  pill.key === 'draft' }"
          @click="activeStatus = pill.key"
        >
          <i :class="pill.icon + ' me-1'"></i>
          {{ pill.label }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-2x text-primary-emphasis"></i>
      <p class="mt-2 text-muted">Buscando seus eventos...</p>
    </div>

    <!-- ── Lista unificada ──────────────────────────────── -->
    <div v-if="!loading" class="mt-0">

      <!-- Nenhum evento cadastrado -->
      <div v-if="allEvents.length === 0" class="empty-state text-center py-5">
        <i class="fas fa-calendar-check fa-2x text-muted mb-3 d-block"></i>
        <p class="text-muted mb-3">Nenhum evento cadastrado ainda.</p>
        <router-link to="/my-account/add" class="empty-state-link">
          Criar meu primeiro evento
        </router-link>
      </div>

      <!-- Lista filtrada -->
      <div v-else>

        <!-- Evento DRAFT → visual opaco + abre BottomModal -->
        <template v-if="activeStatus === 'draft'">
          <div
            v-for="event in filteredEvents"
            :key="event.id"
            class="event-card-draft-wrapper"
            @click="openDraftModal(event)"
            role="button"
          >
            <EventCard
              :event="event"
              class="event-card--draft"
            />
            <span class="event-card-draft-badge">
              <i class="fas fa-lock me-1"></i> Pendente
            </span>
            <span
              v-if="event.withdrawal_mode"
              class="withdrawal-mode-badge withdrawal-mode-badge--on-end"
              title="Você recebe ao finalizar o evento"
            >
              <i class="fas fa-flag-checkered me-1"></i>
              Ao finalizar
            </span>
          </div>
        </template>

        <!-- Evento IN-PROGRESS → abre BottomModal com ações -->
        <template v-else-if="activeStatus === 'in-progress'">
          <div
            v-for="event in filteredEvents"
            :key="event.id"
            class="event-card-mode-wrapper"
          >
            <EventCard
              :event="event"
              @cardClick="openActiveEventModal"
            />
            <span
              v-if="event.withdrawal_mode"
              class="withdrawal-mode-badge withdrawal-mode-badge--on-end"
              title="Você recebe ao finalizar o evento"
            >
              <i class="fas fa-flag-checkered me-1"></i>
              Ao finalizar
            </span>
          </div>
        </template>

        <!-- Evento FINISHED → comportamento atual intacto -->
        <template v-else-if="activeStatus === 'finished'">
          <div
            v-for="event in filteredEvents"
            :key="event.id"
            class="event-card-mode-wrapper"
          >
            <EventCard
              :event="event"
              @cardClick="(e) => handleViewDetails(e.id)"
            />
            <span
              v-if="event.withdrawal_mode"
              class="withdrawal-mode-badge withdrawal-mode-badge--on-end"
              title="Você recebe ao finalizar o evento"
            >
              <i class="fas fa-flag-checkered me-1"></i>
              Ao finalizar
            </span>
          </div>
        </template>

      </div>
    </div>

    <!-- ── Modal: evento draft ───────────────────────────── -->
    <EventActionModal
      :visible="showDraftModal"
      :event="selectedDraftEvent"
      :isDraft="true"
      :missingProfile="missingProfile"
      :draftActivationMessage="draftActivationMessage"
      :showFinalize="false"
      @close="showDraftModal = false"
      @after-share="activateTransfersForSharing"
    />

    <!-- ── Modal: evento ativo ────────────────────────── -->
    <EventActionModal
      :visible="showActiveEventModal"
      :event="selectedActiveEvent"
      :isDraft="false"
      :missingProfile="missingProfile"
      :draftActivationMessage="draftActivationMessage"
      :showFinalize="true"
      @close="showActiveEventModal = false"
      @after-share="activateTransfersForSharing"
      @finalize="openFinalizeModal"
    />

    <!-- ── Modal: confirmar encerramento ────────────────── -->
    <BottomModal
      :visible="showFinalizeConfirmation && !isProcessingAction"
      @close="cancelFinalizeEvent"
      :fullHeight="false"
    >
      <template #default>
        <div class="finalize-modal">
          <div class="finalize-modal__icon-wrap">
            <i class="fas fa-triangle-exclamation finalize-modal__icon"></i>
          </div>
          <h5 class="finalize-modal__title">Encerrando "{{ eventToFinalizeName }}" 🎉</h5>
          <p class="finalize-modal__desc">Tudo certo! Ao encerrar, novos presentes não poderão ser adicionados — mas o seu saldo continua disponível para saque normalmente. Você também poderá consultar o histórico do evento a qualquer momento.</p>

          <p class="finalize-modal__confirm-label">Para confirmar, digite <strong>encerrar</strong> abaixo:</p>
          <input
            v-model="finalizeInputText"
            type="text"
            class="finalize-modal__input"
            placeholder="encerrar"
            autocomplete="off"
          />

          <div class="finalize-modal__actions">
            <button class="finalize-modal__btn finalize-modal__btn--cancel" @click="cancelFinalizeEvent">
              Agora não
            </button>
            <button
              class="finalize-modal__btn finalize-modal__btn--confirm"
              :disabled="!isFinalizeConfirmed"
              @click="handleFinalizeEvent"
            >
              <i class="fas fa-circle-xmark me-1"></i> Sim, encerrar
            </button>
          </div>
        </div>
      </template>
    </BottomModal>

    <BottomModal 
      :visible="showDetailsModal" 
      @close="closeDetailsModal"
      :fullHeight="false"
    >
      <template #default>
        <div v-if="currentEventDetails" class="details-content p-3">
          <h5 class="text-center text-primary-emphasis">Resumo do evento</h5>
          <p class="text-center text-muted mb-4 event-name-detail"><strong>{{ currentEventNameForDetails }}</strong></p>
          <div class="details-grid mb-4">
            <div class="detail-item">
              <span class="detail-value text-primary-emphasis fw-bold">
                {{ currentEventDetails.finalParticipants || '0' }}
              </span>
              <span class="detail-label text-muted">Participantes</span>
            </div>
            <div class="detail-item">
              <span class="detail-value text-primary-emphasis fw-bold">
                {{ formatCurrency(currentEventDetails.finalCollected || 0) }}
              </span>
              <span class="detail-label text-muted">Total arrecadado</span>
            </div>
            <div class="detail-item">
              <div class="star-rating">
                <i v-for="n in Math.floor(currentEventDetails.feedbackRating)" 
                   :key="'full-' + n" 
                   class="fas fa-star filled-star"></i>
                
                <i v-if="currentEventDetails.feedbackRating % 1 >= 0.5" 
                   class="fas fa-star-half-alt filled-star"></i> 
                <i v-for="n in (5 - Math.ceil(currentEventDetails.feedbackRating))" 
                   :key="'empty-' + n" 
                   class="fas fa-star"></i>
              </div>
              <span class="detail-label text-muted mt-1">Avaliação Média</span>
            </div>
          </div>
          <div class="d-flex justify-content-center mt-4">
              <button class="btn btn-outline-secondary w-100" @click="closeDetailsModal">
                  <i class="fas fa-times me-2"></i> Fechar
              </button>
            </div>
        </div>
        <div v-else class="text-center p-4">
          <p class="text-muted">Nenhum detalhe disponível para este evento.</p>
        </div>
      </template>
    </BottomModal>

    <BottomModal
      :visible="showTransfersOnboardingModal"
      @close="closeTransfersOnboardingModal"
      :fullHeight="false"
    >
      <template #default>
        <div class="p-3">
          <div class="text-center mb-3">
            <div style="font-size: 2rem;">🔐</div>
            <h5 class="fw-bold mt-2 mb-1 text-primary-emphasis">Finalize seus recebimentos</h5>
            <p class="text-muted small mb-0">
              Seu evento já foi compartilhado. Para liberar saques, finalize a verificação da conta de recebimento.
            </p>
          </div>

          <div class="draft-activation-note mb-3" role="status" aria-live="polite">
            <i class="fas fa-circle-exclamation draft-activation-note__icon" aria-hidden="true"></i>
            <p class="draft-activation-note__text mb-0">
              Pendências atuais: {{ onboardingDueFields.length }} item(ns) para completar.
            </p>
          </div>

          <div v-if="onboardingSession.clientSecret" class="text-muted small mb-3">
            Uma sessão de onboarding foi preparada para você. Clique em "Configurar agora" para continuar.
          </div>

          <div class="d-flex gap-2">
            <button class="btn btn-outline-secondary w-100" @click="closeTransfersOnboardingModal">
              Agora não
            </button>
            <button class="btn btn-color-site w-100" @click="openTransfersOnboarding">
              Configurar agora
            </button>
          </div>
        </div>
      </template>
    </BottomModal>
    
  </div>  

  <FullScreenLoader v-if="isProcessingAction" />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { formatBRL as formatCurrency } from '@/utils/currencyFormatter';
import EventCard from '@/components/EventCard.vue';
import EventActionModal from '@/components/EventActionModal.vue';
import BottomModal from '@/components/BottomModal.vue'; 
import { toast } from 'vue3-toastify'; 
import FullScreenLoader from '@/components/FullScreenLoader.vue';
import api from '@/services/api';
import { useAuthStore } from '@/stores/authStore';

const authStore = useAuthStore();
const router = useRouter();

// Lista unificada
const allEvents = ref([]);

// Listas filtradas por status
const draftEvents      = computed(() => allEvents.value.filter(e => e.status === 'draft'));
const inProgressEvents = computed(() => allEvents.value.filter(e => e.status === 'in-progress'));
const finishedEvents   = computed(() => allEvents.value.filter(e => ['finished', 'processing'].includes(e.status)));

// Pills disponíveis — apenas status que existem na lista
const availablePills = computed(() => {
  const pills = [];
  if (draftEvents.value.length > 0)      pills.push({ key: 'draft',       label: 'Pendentes',    icon: 'fas fa-lock' });
  if (inProgressEvents.value.length > 0) pills.push({ key: 'in-progress', label: 'Em andamento', icon: 'fa-solid fa-bars-progress' });
  if (finishedEvents.value.length > 0)   pills.push({ key: 'finished',    label: 'Finalizados',  icon: 'fas fa-check-circle' });
  return pills;
});

// Status selecionado
const activeStatus = ref(null);

function setInitialStatus() {
  if (draftEvents.value.length > 0)      { activeStatus.value = 'draft';       return; }
  if (inProgressEvents.value.length > 0) { activeStatus.value = 'in-progress'; return; }
  if (finishedEvents.value.length > 0)   { activeStatus.value = 'finished';    return; }
  activeStatus.value = null;
}

// Lista filtrada pela pill ativa
const filteredEvents = computed(() => {
  if (activeStatus.value === 'draft')       return draftEvents.value;
  if (activeStatus.value === 'in-progress') return inProgressEvents.value;
  if (activeStatus.value === 'finished')    return finishedEvents.value;
  return [];
});

// Estado do modal draft
const showDraftModal     = ref(false);
const selectedDraftEvent = ref(null);

function openDraftModal(event) {
  selectedDraftEvent.value = event;
  showDraftModal.value     = true;
}

// Estado do modal de evento ativo
const showActiveEventModal = ref(false);
const selectedActiveEvent  = ref(null);
const activatingTransfers = ref(false);
const hasActivatedTransfers = ref(false);
const showTransfersOnboardingModal = ref(false);
const onboardingDueFields = ref([]);
const onboardingSession = ref({
  clientSecret: '',
  expiresAt: 0,
});


function openActiveEventModal(event) {
  selectedActiveEvent.value = event;
  showActiveEventModal.value = true;
}


async function activateTransfersForSharing() {
  if (hasActivatedTransfers.value || activatingTransfers.value) {
    return;
  }

  activatingTransfers.value = true;
  try {
    const response = await api.post('/users/finance/connected-accounts/stripe/activate-transfers');
    const payload = response?.data || {};

    const providerAccountId = payload.provider_account_id;
    const providerAccountStatus = payload.provider_account_status;

    if (providerAccountId || providerAccountStatus) {
      authStore.user = {
        ...(authStore.user || {}),
        ...(providerAccountId ? { provider_account_id: providerAccountId } : {}),
        ...(providerAccountStatus ? { provider_account_status: providerAccountStatus } : {}),
      };

      authStore.applyBackendEligibilityFlags(payload);
    }

    if (payload.needs_onboarding) {
      onboardingDueFields.value = Array.isArray(payload.currently_due) ? payload.currently_due : [];
      onboardingSession.value = {
        clientSecret: payload.account_session_client_secret || '',
        expiresAt: payload.account_session_expires_at || 0,
      };
      showTransfersOnboardingModal.value = true;
    }

    hasActivatedTransfers.value = true;
  } catch (error) {
    console.warn('Falha ao ativar transfers no compartilhamento:', error);
  } finally {
    activatingTransfers.value = false;
  }
}

function closeTransfersOnboardingModal() {
  showTransfersOnboardingModal.value = false;
}

function openTransfersOnboarding() {
  showTransfersOnboardingModal.value = false;
  authStore.setOnboardingMode(true);
  router.push('/organizer/onboarding/refresh');
}

// Confirmação de encerramento via modal com digitação
const finalizeInputText   = ref('');
const isFinalizeConfirmed = computed(() => finalizeInputText.value.trim().toLowerCase() === 'encerrar');

function openFinalizeModal() {
  const event = selectedActiveEvent.value;
  if (!event) return;
  showActiveEventModal.value = false;
  eventToFinalizeId.value   = event.id;
  eventToFinalizeName.value = event.name;
  finalizeInputText.value   = '';
  showFinalizeConfirmation.value = true;
}

function closeFinalizeModal() {
  cancelFinalizeEvent();
  finalizeInputText.value = '';
}

// Computed: o que falta para ativar o evento
const missingProfile = computed(() => {
  return authStore.normalizedPlatformStatus !== 'active';
});

const draftActivationMessage = computed(() => {
  if (authStore.normalizedPlatformStatus === 'pending') {
    return 'Seu cadastro está em análise. Assim que aprovado, seu evento será liberado automaticamente.';
  }

  return 'Falta concluir seu cadastro. Assim que finalizar, seu evento será liberado automaticamente.';
});
const loading = ref(true); 

const showFinalizeConfirmation = ref(false);
const eventToFinalizeId = ref(null);
const eventToFinalizeName = ref(''); 
const isProcessingAction = ref(false); 
const currentEventNameForDetails = ref('');

const showDetailsModal = ref(false);
const currentEventDetails = ref(null);

const fetchEvents = async () => {
  loading.value = true;
  try {
    const response = await api.get(`/events/draft,in-progress,finished,processing/status`);
    if (response.status !== 200) {
      throw new Error(`Erro ao buscar eventos: Status HTTP ${response.status}`);
    }
    allEvents.value = response.data;
    setInitialStatus();
    
  } catch (error) {
    console.error('Erro ao buscar eventos:', error);
    if (error instanceof Error) {
        toast.error(error.message);
    } else {
        toast.error('Ocorreu um erro ao carregar os eventos.');
    }
  } finally {
    loading.value = false;
  }
};

const confirmFinalizeEvent = (eventId) => {
  const event = inProgressEvents.value.find(e => e.id === eventId);
  if (event) {
    eventToFinalizeId.value = eventId;
    eventToFinalizeName.value = event.name; 
    showFinalizeConfirmation.value = true;
  }
};

const handleFinalizeEvent = async () => {
  if (!eventToFinalizeId.value) return;

  isProcessingAction.value = true; 
  try {
    const response = await api.put(`/events/${eventToFinalizeId.value}/finished`); 

    if (response.status !== 200) {
      throw new Error(`Erro ao finalizar o evento: Status HTTP ${response.status}`);
    }
    
    toast.success(`Evento "${eventToFinalizeName.value}" finalizado com sucesso!`);    
    
    const idx = allEvents.value.findIndex(e => e.id === eventToFinalizeId.value);
    if (idx !== -1) {
      allEvents.value[idx].status = 'finished';
      if (!allEvents.value[idx].details) {
        allEvents.value[idx].details = { finalCollected: 0, finalParticipants: 0, feedbackRating: 0 };
      }
    }
    activeStatus.value = 'finished';

  } catch (error) {
    console.error('Erro ao finalizar evento:', error);
    if (error instanceof Error) {
        toast.error(error.message);
    } else {
        toast.error(`Não foi possível finalizar o evento "${eventToFinalizeName.value}". Tente novamente.`);
    }
  } finally {
    showFinalizeConfirmation.value = false; 
    eventToFinalizeId.value = null;
    eventToFinalizeName.value = '';
    finalizeInputText.value = '';
    isProcessingAction.value = false; 
  }
};

const cancelFinalizeEvent = () => {
  showFinalizeConfirmation.value = false;
  eventToFinalizeId.value = null;
  eventToFinalizeName.value = '';
  finalizeInputText.value = '';
};

const handleReactivateEvent = async (eventId) => {
  isProcessingAction.value = true;
  try {
    const response = await api.put(`/events/${eventId}/reactivate`); 

    if (response.status !== 200) {
      throw new Error(`Erro ao reativar o evento: Status HTTP ${response.status}`);
    }
    
    toast.success(`Evento reativado com sucesso!`);
    const idx = allEvents.value.findIndex(e => e.id === eventId);
    if (idx !== -1) {
      allEvents.value[idx].status = 'in-progress';
    }
    activeStatus.value = 'in-progress';

  } catch (error) {
    console.error('Erro ao reativar evento:', error);
    if (error instanceof Error) {
        toast.error(error.message);
    } else {
        toast.error('Não foi possível reativar o evento. Tente novamente.');
    }
  } finally {
    isProcessingAction.value = false;
  }
};

const handleViewDetails = (eventId) => {
  const event = allEvents.value.find(e => e.id === eventId);
  if (event && event.details) {
    currentEventDetails.value = event.details;
    currentEventNameForDetails.value = event.name;
    showDetailsModal.value = true;
  } else {
    toast.info('Detalhes do evento não disponíveis.');
  }
};

const closeDetailsModal = () => {
  showDetailsModal.value = false;
  currentEventDetails.value = null;
  currentEventNameForDetails.value = '';
};

onMounted(() => {
  fetchEvents(); 
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  height: auto;
  width: 100%;
  max-width: 700px; /* Mantém a largura máxima do conteúdo */
  margin: 0 auto; /* Centraliza o container */
}

@media (max-width: 767.98px) {
  .page {    
    margin-top: 0rem;
    min-width: inherit;
  }
}

/* Removido .my-events-container, pois .page já está fazendo o papel de container principal */

.text-primary-emphasis {
  color: #32355d !important;
}

.description-text {
  font-size: 0.95rem;
  line-height: 1.5;
  color: #6c757d; 
}

/* ── Pills ─────────────────────────────────────────────── */
.status-pills__label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #9e9e9e;
  margin-bottom: 10px;
}
.status-pills__list {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 99px;
  border: 1.5px solid #e0e0e0;
  background: #fff;
  color: #6c757d;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
}
.status-pill:hover {
  background: #ff50a3;
  border-color: #ff50a3;
  color: #fff;
}
.status-pill--active {
  background: #ff50a3;
  border-color: #ff50a3;
  color: #fff;
}
.status-pill--active i {
  background: #ff50a3;
  border-color: #ff50a3;
  color: #fff;
}
.status-pill--draft.status-pill--active {
  background: #9e9e9e;
  border-color: #9e9e9e;
  color: #fff;
}
.status-pill--draft.status-pill--active i {
  color: #fff;
}

/* ── Card draft ─────────────────────────────────────────── */
.event-card-draft-wrapper {
  position: relative;
  cursor: pointer;
  margin-bottom: 12px;
}

.event-card-mode-wrapper {
  position: relative;
  margin-bottom: 12px;
}
.event-card--draft {
  opacity: 0.6;
  filter: grayscale(30%);
  border: 1.5px solid #d0d0d0 !important;
  pointer-events: none;
  transition: opacity 0.2s;
}
.event-card-draft-wrapper:hover .event-card--draft {
  opacity: 0.8;
}
.event-card-draft-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #f0f0f0;
  color: #9e9e9e;
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 99px;
  padding: 3px 10px;
}

.withdrawal-mode-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  position: absolute;
  top: 10px;
  right: 10px;
}

.withdrawal-mode-badge--per-payment {
  color: #1976D2;
  background-color: #e3f2fd;
}

.withdrawal-mode-badge--on-end {
  color: #7b1fa2;
  background-color: #f3e5f5;
}

/* ── Draft pending items (BottomModal) ────────────────── */
.draft-pending-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  margin-bottom: 10px;
}
.draft-pending-item__icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff3e0;
  color: #ff9800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
}
.draft-pending-item__content { flex: 1; }
.draft-pending-item__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #32355d;
  margin: 0 0 2px;
}
.draft-pending-item__subtitle {
  font-size: 0.78rem;
  color: #9e9e9e;
  margin: 0;
}
.btn-outline-pink {
  border-color: #ff50a3;
  color: #ff50a3;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}
.btn-outline-pink:hover {
  background-color: #ff50a3;
  color: #fff;
}

/* ── Tabs (mantidas para não quebrar caso ainda referenciadas) ── */
.event-tabs {
  display: flex;
  background-color: #f0f0f0;   
  box-shadow: 0 2px 4px rgba(0,0,0,0.05); 
  /* NOVOS ESTILOS PARA TORNAR AS TABS STICKY */
  position: sticky; /* Habilita o comportamento sticky */
  top: 0px; /* Gruda no topo quando a rolagem atinge 0px do topo da viewport */
  z-index: 1000; /* Garante que as tabs fiquem acima de outros conteúdos ao rolar */
  margin-bottom: 4px; /* Ajusta a margem inferior para que os cards abaixo não fiquem colados */
}

.tab-item {
  flex-grow: 1; 
  padding: 15px 20px;
  border: none;
  background-color: #fff; 
  color: #8f9093; 
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.05rem;
  display: flex; 
  align-items: center;
  justify-content: center; 
  border-radius: 0;
}

.tab-item:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.tab-item:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

.tab-item i {
  color: #8f9093; 
  transition: color 0.3s ease;
}

.tab-item:hover {
  background-color: #f7f7f7; 
  color: #32355d; 
}

.tab-item.active {
  background-color: #ff50a3; 
  color: #fff; 
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.1); 
}

.tab-item.active i {
  color: #fff; 
}

.tab-item i.me-2 {
    margin-right: 0.5rem;
}

.empty-state-link {
  color: #ff50a3;
  font-weight: 700;
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 80, 163, 0.35);
  padding-bottom: 1px;
  transition: color 0.18s ease, border-color 0.18s ease;
}

.empty-state-link:hover {
  color: #e64593;
  border-color: rgba(230, 69, 147, 0.55);
}

/* Estilos para o conteúdo da modal de confirmação */
.confirmation-content h5, .details-content h5 { 
  font-size: 1.25rem;
  font-weight: 600;
}

.confirmation-content p, .details-content p { 
  font-size: 0.95rem;
}

/* ── Modal encerramento ─────────────────────────────────── */
.finalize-modal {
  padding: 0.5rem 0.75rem 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.finalize-modal__icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fff4f4;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.85rem;
}

.finalize-modal__icon {
  font-size: 1.4rem;
  color: #dc3545;
}

.finalize-modal__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 0.5rem;
}

.finalize-modal__desc {
  font-size: 0.83rem;
  color: #6c757d;
  line-height: 1.5;
  margin-bottom: 1.1rem;
  max-width: 320px;
}

.finalize-modal__confirm-label {
  font-size: 0.82rem;
  color: #555;
  margin-bottom: 0.5rem;
  width: 100%;
  text-align: left;
}

.finalize-modal__input {
  width: 100%;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 0.9rem;
  outline: none;
  font-family: inherit;
  margin-bottom: 1rem;
  transition: border-color 0.15s;
}

.finalize-modal__input:focus {
  border-color: #dc3545;
}

.finalize-modal__actions {
  display: flex;
  gap: 8px;
  width: 100%;
}

.finalize-modal__btn {
  flex: 1;
  height: 46px;
  border-radius: 50px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: opacity 0.15s, transform 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.finalize-modal__btn--cancel {
  background: #f4f4f4;
  color: #555;
}

.finalize-modal__btn--cancel:hover {
  background: #eaeaea;
}

.finalize-modal__btn--confirm {
  background: #dc3545;
  color: #fff;
}

.finalize-modal__btn--confirm:hover:not(:disabled) {
  opacity: 0.88;
  transform: translateY(-1px);
}

.finalize-modal__btn--confirm:disabled {
  background: #f0a0a8;
  cursor: not-allowed;
}

/* NOVOS ESTILOS para a modal de detalhes */
.details-grid {
  display: flex;
  flex-direction: column; 
  gap: 1.5rem; 
  align-items: center; 
  text-align: center;
}

.detail-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.detail-value {
  font-size: 1.6rem; 
  margin-bottom: 0.3rem; 
}

.detail-label {
  font-size: 0.9rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.star-rating {
  font-size: 1.4rem; 
  color: #e0e0e0; 
  margin-bottom: 0.3rem;
}

.star-rating .filled-star {
  color: #FFC107; 
}

/* NOVO: Estilo para o nome do evento na modal de detalhes */
.event-name-detail {
  font-size: 1rem; 
  font-weight: 500; 
  color: #495057; 
  margin-top: -0.5rem; 
}

/* Novas classes de botão conforme o modelo padrão */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  white-space: nowrap;
}

.btn-color-site {
  background-color: #ff50a3; 
  color: #fff;
  border: 1px solid #ff50a3;
}
.btn-color-site:hover {
  background-color: #e64593;
  border-color: #e64593;
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.btn-outline-secondary {
  background-color: transparent;
  color: #6c757d; 
  border: 1px solid #ced4da; 
}
.btn-outline-secondary:hover {
  background-color: #f8f9fa; 
  border-color: #adb5bd; 
  color: #495057; 
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}

.btn i.me-2 {
  margin-right: 0.5rem;
}


/* Danger outline for finalize */
.btn-danger-outline {
  background-color: transparent;
  color: #c0392b;
  border: 1.5px solid #f5b7b1;
  border-radius: 0.5rem;
  padding: 0.65rem 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
}

.btn-danger-outline:hover {
  background-color: #fdf3f2;
  border-color: #e74c3c;
}

.draft-activation-note {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #ffe3b3;
  background: #fff8ea;
  border-radius: 10px;
  padding: 10px 12px;
}

.draft-activation-note__icon {
  color: #d48806;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.draft-activation-note__text {
  color: #6f5b2a;
  font-size: 0.82rem;
  line-height: 1.4;
  text-align: left;
}

</style>